// Logical field groupings for Metroid Prime: Origins slot saves (Samus / Fusion).

const AREA_DEFS = [
  {
    id: "area-frigate",
    label: "Frigate Orpheon",
    test: (name) =>
      /fri_/i.test(name) || /frigate orpheon/i.test(name) || /crashed frigate/i.test(name),
  },
  {
    id: "area-tallon",
    label: "Tallon Overworld",
    test: (name) => /tal_/i.test(name) || /tallon overworld/i.test(name),
  },
  {
    id: "area-chozo",
    label: "Chozo Ruins",
    // Elder Plasma: the Hall of the Elders plasma slot (newer game versions).
    test: (name) => /cho_/i.test(name) || /chozo ruins/i.test(name) || name === "Elder Plasma",
  },
  {
    id: "area-magmoor",
    label: "Magmoor Caverns",
    test: (name) => /mag_/i.test(name) || /magmoor/i.test(name),
  },
  {
    id: "area-phendrana",
    label: "Phendrana Drifts",
    test: (name) => /phe_/i.test(name) || /phendrana/i.test(name),
  },
  {
    id: "area-mines",
    label: "Phazon Mines",
    test: (name) => /pha_/i.test(name) || /phazon mines/i.test(name),
  },
  {
    id: "area-crater",
    label: "Impact Crater",
    test: (name) => /imp_/i.test(name) || /impact crater/i.test(name),
  },
]

const NAME_GROUPS = [
  {
    id: "status",
    label: "Status",
    open: true,
    names: [
      "Area Name",
      "Save Area",
      "Save Room",
      "Save X",
      "Save Y",
      "Teleport X",
      "Teleport Y",
      "Beam",
      "Energy",
      "Hours",
      "Minutes",
      "Seconds",
      "Milliseconds",
      "Time String",
      // Wall-clock play time and session count (newer game versions only).
      "Real Hours",
      "Real Minutes",
      "Real Seconds",
      "Real Milliseconds",
      "Real Time String",
      "Sessions",
      "Deaths",
      "Costume",
      "Classic Mode",
      "New Game",
      "Hint Number",
      "Hint X",
      "Hint Y",
      "Active Hint",
      "Hint On",
      "Hint Area",
      "Hint Time",
      "Hint Timer",
    ],
  },
  {
    id: "ammo",
    label: "Ammo & tanks",
    open: true,
    names: [
      "Missiles",
      "Missiles Max",
      "Power Bombs",
      "Power Bombs Max",
      "Power Bombs MaX",
      "Energy Tanks",
      "Energy Tanks Max",
      "Total Energy",
      "Total Energy Max",
      "Energy Tank",
    ],
  },
  {
    id: "suits",
    label: "Suits",
    open: true,
    names: ["Power Suit", "Varia Suit", "Gravity Suit", "Phazon Suit", "Phazon Suit Ownership Contract"],
  },
  {
    id: "weapons",
    label: "Beams, missiles & combos",
    open: true,
    names: [
      "Power Beam",
      "Wave Beam",
      "Ice Beam",
      "Plasma Beam",
      "Spazer",
      "SpazEr",
      "Charge Beam",
      "Missile Launcher",
      "Super Missile",
      "Ice Spreader",
      "Wavebuster",
      "Incinerator",
    ],
  },
  {
    id: "movement",
    label: "Morph & movement",
    open: true,
    names: [
      "Morph Ball",
      "Morph Ball Bomb",
      "Boost Ball",
      "Spider Ball",
      "Space Jump Boots",
      "Screw Attack",
      "Grapple Beam",
    ],
  },
  {
    id: "aeon",
    label: "Aeon Powers",
    open: true,
    // `Aeon` lists the powers collected, `Aeon Status` which are equipped; each power's own
    // field mirrors its equipped flag.
    names: [
      "Aeon",
      "Aeon Status",
      "Aqua-Recovery",
      "Auto-Logger",
      "Blaze Missile",
      "Blazing Spider",
      "Boost Charger",
      "Boost Extender",
      "Boost-Sprint",
      "Bountiful Pickups",
      "Chain Missile",
      "Charge Module",
      "Chip Bomb",
      "Cleeted Boots",
      "Damage Discharge",
      "Death Ball",
      "Debris Scope",
      "Energy Generator",
      "Flare Ball",
      "Flare Bomb",
      "Flare Power Bomb",
      "Frost Bomb",
      "Frost Missile",
      "Frost Power Bomb",
      "Gatling Beam",
      "Hefty Beam",
      "Maximum Power",
      "Mine Bomb",
      "Missile Generator",
      "Offensive Boost",
      "Phazon Generator",
      "Phazon Shield",
      "Pickup-Poison",
      "Power Bomb Generator",
      "Proximity Beam",
      "Quick Bombs",
      "Reinforced Suit",
      "Ricochet Cannon",
      "Screw Blizzard",
      "Screw Inferno",
      "Screw Shock",
      "Seeker Beam",
      "Seeker Missile",
      "Shock Missile",
      "Side-Dash",
      "Speed-Ball",
      "Static Bomb",
      "Static Power Bomb",
      "Swift Boots",
      "Velocity Beam",
    ],
  },
  {
    id: "artifacts",
    label: "Artifacts",
    open: true,
    names: Array.from({ length: 12 }, (_, i) => `Artifact ${i + 1}`),
  },
  {
    id: "run-stats",
    label: "Run stats",
    open: false,
    names: [
      "Collection",
      "Completion",
      "Map Completion",
      "Rooms Discovered",
      "Rooms Explored",
      "Damage Dealt",
      "Damage Taken",
      "Distance Traveled",
      "Enemies Killed",
      "Doors Opened",
      "Blocks Broken",
      "Bombs Used",
      "Missiles Fired",
      "Power Bombs Used",
      "Shots Fired",
      "Attempted Scans",
      "Successful Scans",
      "Beam Changes",
      "Beam Combos Used",
      "Grapples",
      "Successful Grapples",
      "Save Room Uses",
    ],
  },
]

const PATTERN_GROUPS = [
  {
    id: "logbook",
    label: "Logbook",
    open: false,
    test: (name) =>
      /^Logbook(\s|$)/i.test(name) ||
      / Logs$/i.test(name) ||
      /^(Creatures|Chozo Lore|Chozo Artifacts)$/i.test(name),
  },
  {
    id: "markers",
    label: "Markers & hints",
    open: false,
    test: (name) => /^Marker \d+/i.test(name) || /^Hint (No\.\d+|Delay)$/i.test(name),
  },
  {
    id: "bosses",
    label: "Bosses & defeats",
    open: false,
    test: (name) =>
      /Defeated/i.test(name) ||
      /^Killed /i.test(name) ||
      /^Mama Sheegoth$/i.test(name) ||
      /^Sheegoths Dead$/i.test(name),
  },
  {
    id: "maps",
    label: "Maps",
    open: false,
    test: (name) => / Map( Percent| [XY])?$/i.test(name),
  },
]

// Matched before area buckets so room-coded expansions aren't buried in Chozo/Mines/etc.
const WORLD_PICKUPS = {
  id: "world-pickups",
  label: "World pickups",
  open: false,
  test: (name) =>
    /^Missiles Max .+\bx-?\d+/i.test(name) ||
    /^Power Bomb .+\bx-?\d+/i.test(name) ||
    /^Energy Tanks Max .+\bx-?\d+/i.test(name) ||
    /^Energy Tank .+\bx-?\d+/i.test(name),
}

function buildNameLookup() {
  const lookup = new Map()
  for (const group of NAME_GROUPS) {
    for (const name of group.names) {
      lookup.set(name, group.id)
    }
  }
  return lookup
}

const NAME_LOOKUP = buildNameLookup()

/**
 * @param {Array<{ name: string }>} fields
 * @returns {Array<{ id: string, label: string, open: boolean, fields: object[] }>}
 */
export function groupSlotFields(fields) {
  const buckets = new Map()

  const ensure = (id, label, open) => {
    if (!buckets.has(id)) buckets.set(id, { id, label, open, fields: [] })
    return buckets.get(id)
  }

  for (const group of NAME_GROUPS) ensure(group.id, group.label, group.open)
  ensure(WORLD_PICKUPS.id, WORLD_PICKUPS.label, WORLD_PICKUPS.open)
  for (const area of AREA_DEFS) ensure(area.id, area.label, false)
  for (const group of PATTERN_GROUPS) ensure(group.id, group.label, group.open)
  ensure("other", "Other", false)

  for (const field of fields) {
    const name = field.name
    const byName = NAME_LOOKUP.get(name)
    if (byName) {
      buckets.get(byName).fields.push(field)
      continue
    }

    if (WORLD_PICKUPS.test(name)) {
      buckets.get(WORLD_PICKUPS.id).fields.push(field)
      continue
    }

    // Newer versions also key each artifact by its room ("Artifact 4 cho_Sunchamber x320 y224").
    if (/^Artifact \d+ /.test(name)) {
      buckets.get("artifacts").fields.push(field)
      continue
    }

    const area = AREA_DEFS.find((def) => def.test(name))
    if (area) {
      buckets.get(area.id).fields.push(field)
      continue
    }

    const patterned = PATTERN_GROUPS.find((def) => def.test(name))
    if (patterned) {
      buckets.get(patterned.id).fields.push(field)
      continue
    }

    buckets.get("other").fields.push(field)
  }

  const order = [
    ...NAME_GROUPS.map((g) => g.id),
    WORLD_PICKUPS.id,
    ...AREA_DEFS.map((g) => g.id),
    ...PATTERN_GROUPS.map((g) => g.id),
    "other",
  ]

  return order
    .map((id) => buckets.get(id))
    .filter((group) => group && group.fields.length > 0)
    .map((group) => ({
      ...group,
      fields: [...group.fields].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base", numeric: true }),
      ),
    }))
}

export function usesFieldGroups(kindId) {
  return kindId === "slot"
}
